import { Check, Plus, X } from 'lucide-react';
import * as React from 'react';
import { COMMON_FAULTS, faultKey } from '@cfl/assets-shared';

import { Button, Input, Label, Textarea } from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { cn } from '@/lib/utils';

/**
 * What is already wrong with an asset.
 *
 * The question this exists to answer gets asked months later, when the holder reports
 * a dead key and IT has to decide whether it happened on their watch. Chips rather
 * than free text for the same reason accessories are chips: "dead keys", "Dead Keys"
 * and "keybord broken" are three different faults to a computer and one fault to a
 * person, and the comparison at repair time is the entire point.
 *
 * ONE control, not two. "Nothing wrong" is the first chip in the same list rather
 * than a separate "I checked this" checkbox beside it - the record underneath still
 * distinguishes "checked and clean" from "nobody looked", but the person filling the
 * form only has to understand a list of things to tick.
 */
export interface ConditionValue {
  faults: string[];
  notes: string;
  /**
   * Whether anybody actually looked. Derived from the chips rather than asked
   * separately - see the note above.
   *
   * False is a real answer and the honest default: it means nothing was recorded, and
   * an empty fault list must never be read as a clean bill of health.
   */
  checked: boolean;
}

export const EMPTY_CONDITION: ConditionValue = { faults: [], notes: '', checked: false };

export function ConditionPicker({
  value,
  onChange,
  label = 'Condition — anything already wrong?',
  hint,
}: {
  value: ConditionValue;
  onChange: (next: ConditionValue) => void;
  label?: string;
  hint?: string;
}) {
  const [custom, setCustom] = React.useState('');

  const { faults, notes, checked } = value;

  // "Nothing wrong" is not stored: it IS checked-with-no-faults. Keeping it derived
  // means the two can never disagree, which a separate boolean would eventually do.
  const nothingWrong = checked && faults.length === 0;

  const isOn = (name: string) => faults.some((f) => faultKey(f) === faultKey(name));

  const toggleFault = (name: string) => {
    const next = isOn(name)
      ? faults.filter((f) => faultKey(f) !== faultKey(name))
      : [...faults, name];
    // Ticking anything at all means somebody looked. Un-ticking the last one leaves
    // "checked, nothing wrong", which is a real and useful answer.
    onChange({ ...value, faults: next, checked: true });
  };

  const toggleNothingWrong = () => {
    // A plain toggle, so a mis-click is undoable. Turning it off returns the record to
    // "nobody looked", which is what it was.
    onChange(
      nothingWrong
        ? { ...value, checked: notes.trim() !== '' }
        : { ...value, faults: [], checked: true },
    );
  };

  const addCustom = () => {
    const name = custom.trim();
    if (name === '' || isOn(name)) {
      setCustom('');
      return;
    }
    onChange({ ...value, faults: [...faults, name], checked: true });
    setCustom('');
  };

  const extras = faults.filter((f) => !COMMON_FAULTS.some((o) => faultKey(o) === faultKey(f)));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      <div className="flex flex-wrap gap-1.5">
        <Hint label={'Nothing is wrong with it'}><button
          type="button"
          onClick={toggleNothingWrong}
          aria-pressed={nothingWrong}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
            nothingWrong
              ? 'border-success bg-success/10 font-medium text-success'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {nothingWrong && <Check className="size-3" />}
          Nothing wrong
        </button></Hint>

        {COMMON_FAULTS.map((name) => (
          <Chip key={name} label={name} active={isOn(name)} onClick={() => toggleFault(name)} />
        ))}
        {extras.map((name) => (
          <Chip key={name} label={name} active custom onClick={() => toggleFault(name)} />
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          className="h-8 max-w-[220px] text-sm"
          placeholder="Something else…"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            // Otherwise Enter submits the surrounding form with a half-typed fault
            // still sitting in the box.
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <Button hint={"Add this condition note"} type="button" size="sm" variant="secondary" onClick={addCustom} className="h-8">
          <Plus className="mr-1 size-3.5" /> Add
        </Button>
      </div>

      <Textarea
        rows={2}
        className="text-sm"
        placeholder="Anything else worth knowing — which keys, how bad the scratch is…"
        value={notes}
        onChange={(e) =>
          onChange({
            ...value,
            notes: e.target.value,
            // Typing a note is looking at the thing.
            checked: checked || e.target.value.trim() !== '',
          })
        }
      />

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {hint ??
          'Recorded against this handover, so a repair reported later can be judged against what was already wrong on the day. Leave it blank if nobody has checked — that is recorded as “not checked”, not as “nothing wrong”.'}
      </p>
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
  custom,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  custom?: boolean;
}) {
  return (
    <Hint label={'Add or remove this condition note'}><button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-warning bg-warning/10 font-medium text-warning'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
      {active && custom && <X className="size-3 opacity-60" />}
    </button></Hint>
  );
}
